import { action, makeAutoObservable, reaction, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import dayjs, { Dayjs } from "dayjs";
import EmployeesStore from "./EmployeesStore";
import { TimeReport } from "../api/TimeReportsApiClient";
import ApplicationStore from "./ApplicationStore";
import ProjectsStore from "./ProjectsStore";
import { Roles } from "../enums/Roles";
import { Employee } from "../models/Employee";

class TimeReportStore {
  ApiStore!: ApiStore;
  ApplicationStore!: ApplicationStore;
  EmployeesStore!: EmployeesStore;
  ProjectsStore!: ProjectsStore;

  isFull: boolean = false;
  isLoading: boolean = true;
  lastUpdateTimestamp: number | null = null;

  reports: TimeReport[];
  reportsForFinancier: { log: TimeReport, payment: number }[];
  timeByMe: (TimeReport | { log: TimeReport; payment: number })[] = [];
  timeByUsers: {
    key: string | number;
    user: Employee | undefined;
    reports: TimeReport[];
    totalHours: number;
    totalAmount: number;
  }[] = [];
  timeByProjects: {
    key: string | number;
    project: any;
    reports: TimeReport[];
  }[] = [];

  myTime: string;
  mySalary: string;
  startDate: Dayjs = dayjs().startOf("month").local();
  endDate: Dayjs = dayjs().endOf("month").local();


  constructor(apiStore: ApiStore, applicationStore: ApplicationStore, employeesStore: EmployeesStore, projectsStore: ProjectsStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.ApplicationStore = applicationStore;
    this.EmployeesStore = employeesStore;
    this.ProjectsStore = projectsStore;

    this.reports = [];
    this.reportsForFinancier = [];
    this.myTime = "00:00:00";
    this.mySalary = "00";


    reaction(
      () => [this.startDate, this.endDate],
      ([start, end]) => {
        if (start.isValid() && end.isValid()) {
          this.getAllByPeriod();
        }
      }
    );
  }

  shouldFetchData(refreshIntervalMs: number = 10000): boolean {
    if (!this.lastUpdateTimestamp) {
      this.lastUpdateTimestamp = Date.now();
      return true
    }
    const now = Date.now();
    let time = now - this.lastUpdateTimestamp;
    let status = time > refreshIntervalMs;
    return status;
  }

  get isFinancier() {
    return this.ApplicationStore.user?.roles?.some(
      (r) => r.id === Roles.Owner || r.id === Roles.Financier
    ) ?? false;
  }

  setFullScreen(isFull: boolean) {
    this.isFull = isFull;
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setStartDate = (date: Dayjs) => {
    this.startDate = date;
  };

  setEndDate = (date: Dayjs) => {
    this.endDate = date;
  };

  add = async (
    projectId: string,
    time: string,
    endTime: string,
    userId: string,
    description: string
  ) => {
    this.isLoading = true;
    const response = await this.ApiStore.TimeReportsApiClient.addTime(
      projectId,
      time,
      endTime,
      userId,
      description
    );

    // Refresh data after adding
    if (response) {
      await this.getAllByPeriod(true);
    }

    this.isLoading = false;
    return response;
  };

  edit = async (id: string, time: string, endDate: string, description: string) => {
    this.isLoading = true;
    const response = await this.ApiStore.TimeReportsApiClient.editTime(id, {
      totalTime: time,
      endDate: endDate,
      description,
    });

    // Refresh data after editing
    if (response) {
      await this.getAllByPeriod(true);
    }

    this.isLoading = false;
    return response;
  };

  filterByUserIdFinancier = (userId: string) => {
    return this.reportsForFinancier.filter(el => el.log.userId === userId)
  }

  filterByUserId = (userId: string) => {
    return this.reports.filter((el) => el.userId === userId);
  };

  filterByProjectId = (projectId: string) => {
    return this.reports.filter((el) => el.projectId === projectId);
  };

  startTimer = async (projectId: string) => {
    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.startTimer(projectId).catch(() => {
      this.isLoading = false;
      return false;
    });

    // Refresh data after starting timer
    if (result !== false) {
      await this.getAllByPeriod(true);
      if (this.isFinancier) {
        await this.getForFinancier(true);
      }
    }

    this.isLoading = false;
    return result !== false;
  };

  stopTimer = async (projectId: string, description: string) => {
    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.stopTimer(projectId, description)
      .catch(() => {
        this.isLoading = false;
        return false;
      });

    // Refresh data after stopping timer
    if (result !== false) {
      await this.getAllByPeriod(true);
      if (this.isFinancier) {
        await this.getForFinancier(true);
      }
      this.updateGroups(this.ApplicationStore.user?.id || "", this.EmployeesStore, this.ProjectsStore);
    }

    this.isLoading = false;
    return result !== false;
  };

  getTimer = async (userId: string) => {
    this.isLoading = true;
    let local_reports: any = this.reports;

    // Ensure we have fresh data
    if (!this.reports || this.reports.length == 0) {
      local_reports = await this.getAllByPeriod(true);
    }

    const timer = local_reports?.find((report: any) => {
      const isFinancier = report.log !== undefined;

      if (isFinancier) {
        return (!report.log.endTime || report.log.endTime === null || report.log.totalTime === null) &&
          report.log.userId.toString() === userId.toString();
      } else {
        return (!report.endTime || report.endTime === null || report.totalTime === null) &&
          report.userId === userId;
      }
    });

    this.isLoading = false;
    return timer;
  }

  deleteTimer = async (id: string) => {
    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.deleteTimer(
      id
    ).catch(() => {
      this.isLoading = false;
      return false
    });

    // Refresh data after deleting timer
    if (result) {
      await this.getAllByPeriod(true);
    }

    this.isLoading = false;
    return result;
  }

  filterByProjectIdFinancier = (projectId: string) => {
    return this.reportsForFinancier.filter(el => el.log.projectId === projectId)
  }

  getAllByPeriod = async (forceFetchData?: boolean) => {
    // Skip if not forcing and data was recently fetched
    if (!forceFetchData && !this.shouldFetchData(5000)) {
      return this.reports;
    }

    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.getAllByPeriod(
      this.startDate.format('YYYY-MM-DD'),
      this.endDate.format('YYYY-MM-DD')
    ).catch(() => {
      this.isLoading = false;
      return null;
    });

    if (result) {
      // Update observable state using runInAction for MobX
      runInAction(() => {
        this.reports = [...result];
        this.lastUpdateTimestamp = Date.now();
      });
    }

    this.isLoading = false;
    return result;
  };

  getMyTime = async (userId: string) => {
    this.isLoading = true;

    const result = await this.ApiStore.TimeReportsApiClient.getByUserId(
      userId
    ).finally(() => {
      this.isLoading = false;
    });

    if (result) {
      runInAction(() => {
        if (result.time) {
          this.myTime = result.time;
          this.mySalary = result.payment;
        } else {
          this.myTime = result;
        }
      });
    }
  };

  getByUserId = async (id: string): Promise<string> => {
    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.getByUserId(
      id
    ).finally(() => {
      this.isLoading = false;
    });
    if (result) {
      return result;
    } else {
      return "00:00:00";
    }
  };

  getByProjectId = async (userId: string, projectId: string) => {
    this.isLoading = true;
    const result = await this.ApiStore.TimeReportsApiClient.getByProjectId(
      userId,
      projectId
    ).catch(() => {
      this.isLoading = false;
    });
    this.isLoading = false;
    return result;
  };

  deleteById = async (id: string) => {
    this.isLoading = true;
    try {
      await this.ApiStore.TimeReportsApiClient.deleteById(id);

      runInAction(() => {
        this.reports = (this.reports ?? []).filter(r => r.id !== id);

        this.reportsForFinancier = (this.reportsForFinancier ?? []).filter(x => {
          const logId = (x as any).log?.id ?? (x as any).id;
          return logId !== id;
        });

        this.lastUpdateTimestamp = Date.now();
      });

      return true;
    } finally {
      this.isLoading = false;
    }
  };

  getForFinancier = async (forceFetchData?: boolean) => {
    if (!forceFetchData && !this.shouldFetchData()) {
      return this.reportsForFinancier;
    }
    this.isLoading = true;
    const result =
      await this.ApiStore.TimeReportsApiClient.getForFinancier().catch(() => {
        this.isLoading = false;
      });
    if (result) {
      runInAction(() => {
        this.reportsForFinancier = result;
        this.lastUpdateTimestamp = Date.now();
      });
    }
    this.isLoading = false;
    return result;
  };

  updateGroups = (currentUserId: string, employeesStore?: any, projectsStore?: any) => {
    this.groupByMe(currentUserId);
    if (employeesStore) this.groupByUsers(employeesStore);
    if (projectsStore) this.groupByProjects(projectsStore);
  };

  groupByMe = (userId: string) => {
    let result: any[];

    if (this.isFinancier) {
      result = this.reportsForFinancier
        .filter(el =>
          el.log.userId === userId &&
          el.log.endTime?.isSameOrAfter(this.startDate, "day") &&
          el.log.endTime?.isSameOrBefore(this.endDate, "day")
        )
        .sort((a, b) => a.log.endTime?.unix() - b.log.endTime?.unix());
    } else {
      result = this.reports
        .filter(el =>
          el.userId === userId &&
          el.endTime?.isSameOrAfter(this.startDate, "day") &&
          el.endTime?.isSameOrBefore(this.endDate, "day")
        )
        .sort((a, b) => a.endTime?.unix() - b.endTime?.unix());
    }

    runInAction(() => {
      this.timeByMe = [...result];
    });
  };

  groupByUsers = (employeesStore: any) => {
    let result: any[] = [];

    if (this.isFinancier) {
      result = this.reportsForFinancier.filter(el =>
        employeesStore.allUsers.some((user: Employee) => user.id === el.log.userId) &&
        el.log.endTime?.isSameOrAfter(this.startDate, "day") &&
        el.log.endTime?.isSameOrBefore(this.endDate, "day")
      );
    } else {
      result = this.reports.filter(el =>
        employeesStore.allUsers.some((user: Employee) => user.id === el.userId) &&
        el.endTime?.isSameOrAfter(this.startDate, "day") &&
        el.endTime?.isSameOrBefore(this.endDate, "day")
      );
    }

    const grouped = result.reduce((acc: any, obj: any) => {
      const log = obj.log ?? obj;
      const userId = log.userId;
      if (!acc[userId]) {
        acc[userId] = {
          key: userId,
          user: employeesStore.allUsers.find((u: any) => u.id === userId),
          reports: [],
          totalHours: 0,
          totalAmount: 0,
        };
      }
      acc[userId].reports.push(log);
      acc[userId].totalHours += parseFloat(log.totalTime ?? "0");
      if (obj.payment) acc[userId].totalAmount += obj.payment;
      return acc;
    }, {});

    runInAction(() => {
      this.timeByUsers = Object.values(grouped);
    });
  };

  groupByProjects = (projectsStore: any) => {
    let result: any[];

    if (this.isFinancier) {
      result = this.reportsForFinancier.filter(el =>
        projectsStore.projects.some((proj: any) => proj.id === el.log.projectId) &&
        el.log.endTime?.isSameOrAfter(this.startDate, "day") &&
        el.log.endTime?.isSameOrBefore(this.endDate, "day")
      );
    } else {
      result = this.reports.filter(el =>
        projectsStore.projects.some((proj: any) => proj.id === el.projectId) &&
        el.endTime?.isSameOrAfter(this.startDate, "day") &&
        el.endTime?.isSameOrBefore(this.endDate, "day")
      );
    }

    const grouped = result.reduce((acc: any, obj: any) => {
      const log = obj.log ?? obj;
      const projectId = log.projectId;
      if (!acc[projectId]) {
        acc[projectId] = {
          key: projectId,
          project: projectsStore.projects.find((p: any) => p.id === projectId),
          reports: [],
        };
      }
      acc[projectId].reports.push(log);
      return acc;
    }, {});

    runInAction(() => {
      this.timeByProjects = Object.values(grouped);
    });
  };
}
export default TimeReportStore;